import { useState } from "react";
import { usePageMeta } from "@/lib/seo";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

export default function LoginPage() {
  usePageMeta("Log In");
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const n = new URLSearchParams(window.location.search).get("next") || "";
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      navigate(n.startsWith("/") && !n.startsWith("//") ? n : user.role === "admin" ? "/admin" : user.role === "demo" ? "/demo/app" : "/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "demo_expired") return navigate("/demo-expired");
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join(" ") : "Login failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center pt-16 px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="overflow-hidden" style={{ height: '60px', width: '260px' }}>
              <img src="https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/qy6rjh9d_Dynamic%20Logo%20for%20Automotive%20Channel%20Remix%20the%20Whip.png" alt="ShopAdvisor" className="w-full h-auto" style={{ marginTop: '-38%', marginBottom: '-38%' }} />
            </div>
          </div>
          <h1 className="text-3xl font-black text-slate-900">Welcome Back</h1>
          <p className="text-slate-500 text-sm mt-1">Sign in to your ShopAdvisor account</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 border border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 text-slate-700 font-medium py-3 rounded-lg transition-colors mb-6" data-testid="google-login-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Sign in with Google
          </button>
          <div className="relative mb-6">
            <Separator className="bg-slate-200" />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-slate-400">or sign in with email</span>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm p-3 rounded-lg" data-testid="login-error">{error}</div>}
            <div>
              <Label className="text-slate-700 text-sm mb-1.5 block">Email</Label>
              <Input type="email" className="bg-white border-slate-300 text-slate-900" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm mb-1.5 block">Password</Label>
              <Input type="password" className="bg-white border-slate-300 text-slate-900" placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50" data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-5">
            Don't have an account? <Link to="/register" className="text-blue-600 hover:text-blue-500 font-medium" data-testid="login-register-link">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
